
import { defineComponent } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  queryChannelTypes,
  queryBrandStores,
  querySalesOffices,
  queryCustomerRelationPage,
  queryCustomersByErpCodes,
  batchSaveConfigs,
  type SalesRatioControlRecord
} from './api';
import { clickUploadExcelFile, exportExcelFile } from '../../custom-widget/MindMap/utils/excel';

let rowKeySeq = 1;

export default defineComponent({
  name: 'SalesRatioControlConfigForm',
  props: {
    moduleName: { type: String, default: 'deliAriesCorePlatform' },
    editRecord: { type: Object, default: null },
    backToList: { type: Function, default: null },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      level: '' as '' | 'channel' | 'shop' | 'customer',
      rows: [] as any[],
      selectAll: false,
      saving: false,
      rowSearchCode: '',
      rowSearchName: '',
      channelOptions: [] as { code: string; name: string }[],
      provinceOptions: [] as { code: string; name: string }[],
      customerOptions: [] as any[],
      // 渠道弹窗
      channelModalVisible: false,
      channelModalKeyword: '',
      channelModalList: [] as { code: string; name: string }[],
      channelModalSelectAll: false,
      channelModalChecked: [] as string[],
      // 省区穿梭弹窗
      provinceModalVisible: false,
      transferChSelected: [] as string[],
      transferPrSelected: [] as string[],
      trChCandSearch: '',
      trChSelSearch: '',
      trPrCandSearch: '',
      trPrSelSearch: '',
      trChCandChecked: [] as string[],
      trChSelChecked: [] as string[],
      trPrCandChecked: [] as string[],
      trPrSelChecked: [] as string[],
      // 客户弹窗
      customerModalVisible: false,
      custModalTab: 'all' as 'all' | 'selected',
      custFilterProvince: '',
      custFilterChannel: '',
      custSearch: '',
      modalSelectedCustCodes: [] as string[],
      // 客户弹窗分页（滞动加载）
      custPage: 1,
      custPageSize: 15,
      custHasMore: false,
      custLoading: false,
      custSearchTimer: null as any,
      // 批量填值
      batchFillVisible: false,
      batchVals: { promo: '', margin: '', provMargin: '', oldProd: '', newProd: '' }
    };
  },
  computed: {
    step2Title(): string {
      if (this.level === 'shop') return '第二步：适用省区范围';
      if (this.level === 'channel') return '第二步：适用渠道范围';
      return '第二步：适用经销商范围';
    },
    emptyColspan(): number {
      if (this.level === 'shop' || this.level === 'channel') return 8;
      return 10;
    },
    visibleRows(): any[] {
      if (this.level !== 'customer') return this.rows;
      const code = this.rowSearchCode.trim().toLowerCase();
      const name = this.rowSearchName.trim().toLowerCase();
      return this.rows.filter((r) => {
        if (code && !`${r.erpCode}`.toLowerCase().includes(code)) return false;
        if (name && !`${r.name}`.toLowerCase().includes(name)) return false;
        return true;
      });
    },
    checkedRowCount(): number {
      return this.rows.filter((r) => r.checked).length;
    },
    chCandItems(): { code: string; name: string }[] {
      const q = this.trChCandSearch.trim().toLowerCase();
      return this.channelOptions
        .filter((c) => !this.transferChSelected.includes(c.name))
        .filter((c) => !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    },
    chSelItems(): { code: string; name: string }[] {
      const q = this.trChSelSearch.trim().toLowerCase();
      return this.channelOptions
        .filter((c) => this.transferChSelected.includes(c.name))
        .filter((c) => !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    },
    prCandItems(): { code: string; name: string }[] {
      const q = this.trPrCandSearch.trim().toLowerCase();
      return this.provinceOptions
        .filter((p) => !this.transferPrSelected.includes(p.code))
        .filter((p) => !q || p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    },
    prSelItems(): { code: string; name: string }[] {
      const q = this.trPrSelSearch.trim().toLowerCase();
      return this.provinceOptions
        .filter((p) => this.transferPrSelected.includes(p.code))
        .filter((p) => !q || p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q));
    },
    custModalRows(): any[] {
      let rows = this.customerOptions;
      if (this.custModalTab === 'selected') {
        rows = rows.filter((c) => this.modalSelectedCustCodes.includes(c.rowKey));
      }
      if (this.custFilterChannel) {
        rows = rows.filter((c) => c.channelTypes && c.channelTypes[0] && c.channelTypes[0].name === this.custFilterChannel);
      }
      return rows;
    }
  },
  async mounted() {
    document.addEventListener('click', this.closeAllRowMenus);
    await this.loadChannelOptions();
    await this.loadProvinceOptions();
    this.applyEditRecord();
  },
  watch: {
    editRecord: {
      handler() {
        this.applyEditRecord();
      },
      deep: true
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeAllRowMenus);
  },
  methods: {
    async loadChannelOptions() {
      try {
        this.channelOptions = await queryChannelTypes(this.moduleName);
      } catch (e) {
        this.channelOptions = [];
      }
    },
    async loadProvinceOptions() {
      try {
        this.provinceOptions = await queryBrandStores(this.moduleName);
      } catch (e) {
        this.provinceOptions = [];
      }
    },
    applyEditRecord() {
      const rec = this.editRecord as any;
      if (!rec) return;
      const map: Record<string, any> = { CHANNEL: 'channel', PROVINCE: 'shop', CUSTOMER: 'customer' };
      this.level = map[rec.controlLevel] || '';
      if (!this.level) return;
      const base = {
        _key: rowKeySeq++,
        checked: false,
        promo: rec.promoSalesRatio ?? '',
        margin: rec.customerMarginRatio ?? '',
        provMargin: rec.provinceMarginRatio ?? '',
        oldProd: rec.oldProductMarginRate ?? '',
        newProd: rec.newProductMarginRate ?? ''
      };
      if (this.level === 'channel') {
        this.rows = [{ ...base, _chOpen: false, channels: rec.channelType ? [{ code: rec.channelTypeCode || '', name: rec.channelType }] : [] }];
      } else if (this.level === 'shop') {
        this.rows = [
          {
            ...base,
            _chOpen: false,
            _soOpen: false,
            provinceCode: rec.provincialBranchCode || '',
            provinceName: rec.provincialBranchName || '',
            channels: rec.channelType ? [{ code: rec.channelTypeCode || '', name: rec.channelType }] : [],
            salesOffices: rec.salesOfficeName && rec.salesOfficeName !== '不限' ? [{ code: rec.salesOfficeCode || '', name: rec.salesOfficeName }] : [],
            soOptions: []
          }
        ];
      } else {
        this.rows = [
          {
            ...base,
            code: rec.customerCode || '',
            name: rec.customerName || '',
            erpCode: rec.customerErpCode || '',
            provinceCode: rec.provincialBranchCode || '',
            provinceName: rec.provincialBranchName || '',
            salesOfficeCode: rec.salesOfficeCode || '',
            channel: rec.channelType || '',
            channelCode: rec.channelTypeCode || '',
            soStr: rec.salesOfficeName || '-'
          }
        ];
      }
    },
    handleLevelChange() {
      this.rows = [];
      this.selectAll = false;
    },
    handleAddRow() {
      if (!this.level) {
        ElMessage.warning('请先选择管控维度');
        return;
      }
      if (this.level === 'channel') this.openChannelModal();
      else if (this.level === 'shop') this.openProvinceModal();
      else this.openCustomerModal();
    },
    toggleSelectAll() {
      this.rows.forEach((r) => (r.checked = this.selectAll));
    },
    deleteSelectedRows() {
      const checked = this.rows.filter((r) => r.checked);
      if (checked.length === 0) {
        ElMessage.warning('请先勾选需要删除的行');
        return;
      }
      ElMessageBox.confirm(`确认要删除选中的 ${checked.length} 行数据吗？`, '提示')
        .then(() => {
          this.rows = this.rows.filter((r) => !r.checked);
          this.selectAll = false;
        })
        .catch(() => undefined);
    },
    // ============ 行内多选 ============
    toggleRowMenu(row: any, key: 'ch' | 'so') {
      const openKey = key === 'ch' ? '_chOpen' : '_soOpen';
      const willOpen = !row[openKey];
      this.closeAllRowMenus();
      row[openKey] = willOpen;
    },
    closeAllRowMenus() {
      this.rows.forEach((r) => {
        if (r._chOpen !== undefined) r._chOpen = false;
        if (r._soOpen !== undefined) r._soOpen = false;
      });
    },
    isSelected(list: any[], name: string): boolean {
      return list.some((x) => x.name === name);
    },
    toggleTag(row: any, key: 'channels' | 'salesOffices', option: { code: string; name: string }) {
      const list = row[key] as any[];
      const idx = list.findIndex((x) => x.name === option.name);
      if (idx >= 0) list.splice(idx, 1);
      else list.push(option);
    },
    removeTag(row: any, key: 'channels' | 'salesOffices', option: { code: string; name: string }) {
      const list = row[key] as any[];
      const idx = list.findIndex((x) => x.name === option.name);
      if (idx >= 0) list.splice(idx, 1);
    },
    // ============ 渠道弹窗 ============
    openChannelModal() {
      this.channelModalKeyword = '';
      this.channelModalChecked = [];
      this.channelModalSelectAll = false;
      this.channelModalList = [...this.channelOptions];
      this.channelModalVisible = true;
    },
    filterChannelModal() {
      const q = this.channelModalKeyword.trim().toLowerCase();
      this.channelModalList = !q
        ? [...this.channelOptions]
        : this.channelOptions.filter((c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q));
    },
    toggleChannelModalAll() {
      this.channelModalChecked = this.channelModalSelectAll ? this.channelModalList.map((c) => c.code) : [];
    },
    confirmAddChannels() {
      if (this.channelModalChecked.length === 0) {
        ElMessage.warning('请至少选择一个渠道');
        return;
      }
      this.channelModalChecked.forEach((code) => {
        const ch = this.channelOptions.find((c) => c.code === code);
        if (!ch) return;
        this.rows.push({
          _key: rowKeySeq++,
          _chOpen: false,
          checked: false,
          channels: [{ code: ch.code, name: ch.name }],
          promo: '',
          margin: '',
          provMargin: '',
          oldProd: '',
          newProd: ''
        });
      });
      this.channelModalVisible = false;
    },
    // ============ 省区穿梭弹窗 ============
    async openProvinceModal() {
      if (this.provinceOptions.length === 0) {
        try {
          this.provinceOptions = await queryBrandStores(this.moduleName);
        } catch (e) {
          this.provinceOptions = [];
        }
      }
      this.transferChSelected = [];
      this.transferPrSelected = [];
      this.trChCandSearch = this.trChSelSearch = this.trPrCandSearch = this.trPrSelSearch = '';
      this.trChCandChecked = [];
      this.trChSelChecked = [];
      this.trPrCandChecked = [];
      this.trPrSelChecked = [];
      this.provinceModalVisible = true;
    },
    toggleTrCandAll(type: 'ch' | 'pr', e: any) {
      const checked = e.target.checked;
      if (type === 'ch') this.trChCandChecked = checked ? this.chCandItems.map((c) => c.name) : [];
      else this.trPrCandChecked = checked ? this.prCandItems.map((p) => p.code) : [];
    },
    toggleTrSelAll(type: 'ch' | 'pr', e: any) {
      const checked = e.target.checked;
      if (type === 'ch') this.trChSelChecked = checked ? this.chSelItems.map((c) => c.name) : [];
      else this.trPrSelChecked = checked ? this.prSelItems.map((p) => p.code) : [];
    },
    transferChRight() {
      this.trChCandChecked.forEach((n) => {
        if (!this.transferChSelected.includes(n)) this.transferChSelected.push(n);
      });
      this.trChCandChecked = [];
    },
    transferChLeft() {
      this.transferChSelected = this.transferChSelected.filter((n) => !this.trChSelChecked.includes(n));
      this.trChSelChecked = [];
    },
    transferPrRight() {
      this.trPrCandChecked.forEach((c) => {
        if (!this.transferPrSelected.includes(c)) this.transferPrSelected.push(c);
      });
      this.trPrCandChecked = [];
    },
    transferPrLeft() {
      this.transferPrSelected = this.transferPrSelected.filter((c) => !this.trPrSelChecked.includes(c));
      this.trPrSelChecked = [];
    },
    async confirmAddProvinces() {
      if (this.transferChSelected.length === 0) {
        ElMessage.warning('请至少在左侧选择一个渠道类型！');
        return;
      }
      if (this.transferPrSelected.length === 0) {
        ElMessage.warning('请至少在右侧选择一个省区！');
        return;
      }
      const channels = this.transferChSelected.map((n) => {
        const opt = this.channelOptions.find((c) => c.name === n);
        return { code: opt ? opt.code : '', name: n };
      });
      for (const code of this.transferPrSelected) {
        const pr = this.provinceOptions.find((p) => p.code === code);
        if (!pr) continue;
        let soOptions: { code: string; name: string }[] = [];
        try {
          soOptions = await querySalesOffices(this.moduleName, { brandStoreCode: pr.code });
        } catch (e) {
          soOptions = [];
        }
        this.rows.push({
          _key: rowKeySeq++,
          _chOpen: false,
          _soOpen: false,
          checked: false,
          provinceCode: pr.code,
          provinceName: pr.name,
          channels: channels.map((c) => ({ ...c })),
          salesOffices: [],
          soOptions,
          promo: '',
          margin: '',
          provMargin: '',
          oldProd: '',
          newProd: ''
        });
      }
      this.provinceModalVisible = false;
    },
    // ============ 客户弹窗 ============
    async openCustomerModal() {
      if (this.provinceOptions.length === 0) {
        try {
          this.provinceOptions = await queryBrandStores(this.moduleName);
        } catch (e) {
          this.provinceOptions = [];
        }
      }
      this.custModalTab = 'all';
      this.custFilterProvince = '';
      this.custFilterChannel = '';
      this.custSearch = '';
      this.modalSelectedCustCodes = [...new Set(this.rows.map((r) => `${r.code}__${r.provinceCode || ''}`))] as string[];
      await this.loadCustomers();
      this.customerModalVisible = true;
    },
    async loadCustomers() {
      // 重置分页并加载第一页
      this.custPage = 1;
      this.custHasMore = false;
      this.customerOptions = [];
      await this.fetchCustomerPage(1);
    },
    onCustSearchInput() {
      if (this.custSearchTimer) {
        clearTimeout(this.custSearchTimer);
      }
      this.custSearchTimer = setTimeout(() => {
        this.loadCustomers();
      }, 300);
    },
    async loadMoreCustomers() {
      if (this.custLoading || !this.custHasMore || this.custModalTab !== 'all') {
        return;
      }
      await this.fetchCustomerPage(this.custPage + 1);
    },
    async fetchCustomerPage(pageNo: number) {
      if (this.custLoading) return;
      this.custLoading = true;
      try {
        const res = await queryCustomerRelationPage(this.moduleName, {
          province: this.custFilterProvince,
          keyword: this.custSearch,
          currentPage: pageNo,
          size: this.custPageSize
        });
        // 后端已排除无合作关系的客户，每条合作关系直接展开为一行
        const opts: any[] = (res.content || []).map((rel: any) => this.makeCustOption(rel));
        if (pageNo <= 1) {
          this.customerOptions = opts;
        } else {
          this.customerOptions = this.customerOptions.concat(opts);
        }
        this.custPage = res.currentPage || pageNo;
        this.custHasMore = this.custPage < (res.totalPages || 0);
      } catch (e) {
        if (pageNo <= 1) this.customerOptions = [];
        this.custHasMore = false;
      } finally {
        this.custLoading = false;
      }
    },
    onCustScroll(e: any) {
      const el = e.target;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.loadMoreCustomers();
      }
    },
    makeCustOption(rel: any): any {
      const store = rel && rel.originPartner;
      const so = rel && rel.salesOffice;
      const code = (rel && rel.targetPartnerCode) || '';
      const storeCode = (rel && rel.originPartnerCode) || (store && store.code) || '';
      const channelName = (rel && rel.customerChannelName) || '';
      return {
        rowKey: `${code}__${storeCode}`,
        code,
        name: (rel && rel.ariesTargetPartnerName) || '',
        erpCode: (rel && rel.customerErpCode) || '',
        channelTypes: channelName ? [{ name: channelName }] : [],
        provinceCode: storeCode,
        provinceName: (store && store.name) || '通用省区',
        salesOfficeCode: (so && so.code) || '',
        salesOfficeName: (so && so.name) || '-'
      };
    },
    switchCustTab(tab: 'all' | 'selected') {
      this.custModalTab = tab;
    },
    custLabel(key: string): string {
      const c = this.customerOptions.find((x) => x.rowKey === key);
      if (!c) return key;
      return c.provinceName && c.provinceName !== '通用省区'
        ? `${c.code} ${c.name}（${c.provinceName}）`
        : `${c.code} ${c.name}`;
    },
    removeCustCode(key: string) {
      this.modalSelectedCustCodes = this.modalSelectedCustCodes.filter((c) => c !== key);
    },
    toggleCustModalAll(e: any) {
      const checked = e.target.checked;
      if (checked) {
        this.custModalRows.forEach((c) => {
          if (!this.modalSelectedCustCodes.includes(c.rowKey)) this.modalSelectedCustCodes.push(c.rowKey);
        });
      } else {
        const keys = this.custModalRows.map((c) => c.rowKey);
        this.modalSelectedCustCodes = this.modalSelectedCustCodes.filter((c) => !keys.includes(c));
      }
    },
    confirmAddCustomers() {
      const selected = this.customerOptions.filter((c) => this.modalSelectedCustCodes.includes(c.rowKey));
      // 保留已有行阈值：按 客户编码 + 店铺编码 组合作为键
      const existing = new Map(this.rows.map((r) => [`${r.code}__${r.provinceCode || ''}`, r]));
      this.rows = selected.map((opt) => {
        const prev = existing.get(opt.rowKey);
        return {
          _key: prev ? prev._key : rowKeySeq++,
          checked: false,
          code: opt.code,
          name: opt.name,
          erpCode: opt.erpCode || '',
          provinceCode: opt.provinceCode || '',
          provinceName: opt.provinceName || '通用省区',
          salesOfficeCode: opt.salesOfficeCode || '',
          soStr: opt.salesOfficeName || '-',
          channel: (opt.channelTypes && opt.channelTypes[0] && opt.channelTypes[0].name) || '不限',
          channelCode: (opt.channelTypes && opt.channelTypes[0] && opt.channelTypes[0].code) || (this.channelOptions.find((c) => c.name === ((opt.channelTypes && opt.channelTypes[0] && opt.channelTypes[0].name) || '')) || {}).code || '',
          promo: prev ? prev.promo : '',
          margin: prev ? prev.margin : '',
          provMargin: prev ? prev.provMargin : '',
          oldProd: prev ? prev.oldProd : '',
          newProd: prev ? prev.newProd : ''
        };
      });
      this.customerModalVisible = false;
    },
    // ============ 导入 ============
    async handleImport() {
      if (!this.level) {
        ElMessage.warning('请先选择管控维度');
        return;
      }
      try {
        const dataList = await clickUploadExcelFile();
        if (!dataList || dataList.length === 0) {
          ElMessage.warning('导入文件数据为空');
          return;
        }
        const { rows: newRows, errors } = this.parseImportData(dataList);
        if (errors.length > 0) {
          ElMessage.error(errors.join('\n'));
          return;
        }
        if (newRows.length === 0) {
          ElMessage.warning('未解析到有效数据，请检查模板格式');
          return;
        }
        // 根据省区编码查询销售办公室，补全中文名称
        await this.enrichSalesOfficeNames(newRows);
        // 根据 ERP 编码查询客户，补全客户名称
        await this.enrichCustomerNames(newRows);
        // 合并到已有行（按关键字段去重，新数据覆盖旧阈值）
        this.mergeImportRows(newRows);
        ElMessage.success(`导入成功，共添加 ${newRows.length} 行数据`);
      } catch (e: any) {
        if (e?.message !== 'No file selected') {
          ElMessage.error(e?.message || '导入失败');
        }
      }
    },
    parseImportData(dataList: any[]): { rows: any[]; errors: string[] } {
      const rows: any[] = [];
      const errors: string[] = [];
      const num = (v: any) => (v === '' || v == null ? '' : String(v).trim());
      let rowIdx = 2; // Excel行号从2开始（1行为表头）
      for (const item of dataList) {
        const promo = num(item['促销销售占比(%)']);
        const margin = num(item['让利占比-客户(%)']);
        const provMargin = num(item['让利占比-省区(%)']);
        const oldProd = num(item['老品让利幅度(%)']);
        const newProd = num(item['新品让利幅度(%)']);
        const thr = { promo, margin, provMargin, oldProd, newProd };
        const chOptByCode = (code: string) => this.channelOptions.find((c) => c.code === code);
        const prOptByCode = (code: string) => this.provinceOptions.find((p) => p.code === code);
        if (this.level === 'channel') {
          const chCode = num(item['渠道类型编码（必填）'] ?? item['渠道类型编码']);
          const chName = num(item['渠道类型']);
          if (!chCode) continue;
          const chOpt = chOptByCode(chCode);
          rows.push({
            _key: rowKeySeq++, _chOpen: false, checked: false,
            channels: [{ code: chCode, name: chName || (chOpt ? chOpt.name : chCode) }], ...thr
          });
        } else if (this.level === 'shop') {
          const provinceCode = num(item['省区编码（必填）']);
          const provinceName = num(item['省区名称']);
          const chCode = num(item['渠道类型编码（必填）']);
          const chName = num(item['渠道类型']);
          const soCode = num(item['销售办公室编码'] ?? item['销售办公室编码（必填）']);
          const soName = num(item['销售办公室名称']);
          const missing: string[] = [];
          if (!provinceCode) missing.push('省区编码');
          if (!chCode) missing.push('渠道类型编码');
          if (missing.length > 0) {
            errors.push(`第${rowIdx}行：${missing.join('、')}不能为空`);
            rowIdx++;
            continue;
          }
          const chOpt = chOptByCode(chCode);
          const prOpt = prOptByCode(provinceCode);
          // 省区维度销售办公室编码非必填，留空表示"全部"
          const salesOffices = soCode
            ? [{ code: soCode, name: soName || soCode }]
            : [{ code: '', name: '全部' }];
          rows.push({
            _key: rowKeySeq++, _chOpen: false, _soOpen: false, checked: false,
            provinceCode, provinceName: provinceName || (prOpt ? prOpt.name : ''),
            channels: [{ code: chCode, name: chName || (chOpt ? chOpt.name : chCode) }],
            salesOffices,
            soOptions: [], ...thr
          });
        } else if (this.level === 'customer') {
          const erpCode = num(item['客户ERP编码（必填）']);
          const name = num(item['客户名称']);
          const provinceCode = num(item['省区编码（必填）']);
          const provinceName = num(item['省区名称']);
          const soCode = num(item['销售办公室编码（必填）']);
          const soName = num(item['销售办公室名称']);
          const chCode = num(item['渠道类型编码（必填）']);
          const channel = num(item['渠道类型']);
          const missing: string[] = [];
          if (!erpCode) missing.push('客户ERP编码');
          if (!provinceCode) missing.push('省区编码');
          if (!soCode) missing.push('销售办公室编码');
          if (!chCode) missing.push('渠道类型编码');
          if (missing.length > 0) {
            errors.push(`第${rowIdx}行：${missing.join('、')}不能为空`);
            rowIdx++;
            continue;
          }
          const chOpt = chOptByCode(chCode);
          const prOpt = prOptByCode(provinceCode);
          rows.push({
            _key: rowKeySeq++, checked: false,
            code: '', name: name || '', erpCode,
            provinceCode, provinceName: provinceName || (prOpt ? prOpt.name : '通用省区'),
            salesOfficeCode: soCode, soStr: soName || '-',
            channel: channel || (chOpt ? chOpt.name : '不限'), channelCode: chCode, ...thr
          });
        }
        rowIdx++;
      }
      return { rows, errors };
    },
    async enrichSalesOfficeNames(newRows: any[]) {
      if (this.level !== 'shop' && this.level !== 'customer') return;
      // 收集所有不重复的省区编码
      const provinceCodes = [...new Set(newRows.map((r: any) => r.provinceCode).filter(Boolean))] as string[];
      if (provinceCodes.length === 0) return;
      // 按省区编码查询销售办公室，构建 soCode -> soName 映射
      const soNameMap = new Map<string, string>();
      const soOptionsMap = new Map<string, any[]>();
      for (const provinceCode of provinceCodes) {
        try {
          const options = await querySalesOffices(this.moduleName, { brandStoreCode: provinceCode });
          soOptionsMap.set(provinceCode, options);
          for (const opt of options) {
            soNameMap.set(opt.code, opt.name || opt.displayName || opt.code);
          }
        } catch (e) {
          soOptionsMap.set(provinceCode, []);
        }
      }
      // 补全每行的销售办公室名称
      for (const row of newRows) {
        const soOptions = soOptionsMap.get(row.provinceCode) || [];
        if (this.level === 'shop') {
          row.soOptions = soOptions;
          if (row.salesOffices && row.salesOffices.length > 0) {
            const so = row.salesOffices[0];
            const optName = soNameMap.get(so.code);
            if (optName && (!so.name || so.name === so.code)) {
              so.name = optName;
            }
          }
        } else if (this.level === 'customer') {
          if (row.salesOfficeCode) {
            const optName = soNameMap.get(row.salesOfficeCode);
            if (optName && (!row.soStr || row.soStr === '-' || row.soStr === row.salesOfficeCode)) {
              row.soStr = optName;
            }
          }
        }
      }
    },
    async enrichCustomerNames(newRows: any[]) {
      if (this.level !== 'customer') return;
      // 收集所有不重复的客户 ERP 编码
      const erpCodes = [...new Set(newRows.map((r: any) => r.erpCode).filter(Boolean))] as string[];
      if (erpCodes.length === 0) return;
      // 按 ERP 编码批量查询客户信息
      const customers = await queryCustomersByErpCodes(this.moduleName, erpCodes);
      const custMap = new Map<string, { code: string; name: string }>();
      for (const c of customers) {
        if (c.erpCode) custMap.set(c.erpCode, { code: c.code, name: c.name });
      }
      // 补全每行的客户名称和内部编码
      for (const row of newRows) {
        const cust = custMap.get(row.erpCode);
        if (cust) {
          if (!row.code) row.code = cust.code;
          if (!row.name) row.name = cust.name;
        }
      }
    },
    mergeImportRows(newRows: any[]) {
      if (this.level === 'channel') {
        // 按渠道编码去重
        const existing = new Map(this.rows.map((r) => [r.channels.map((c: any) => c.code).join(','), r]));
        for (const nr of newRows) {
          const key = nr.channels.map((c: any) => c.code).join(',');
          if (existing.has(key)) {
            Object.assign(existing.get(key), nr);
          } else {
            this.rows.push(nr);
            existing.set(key, nr);
          }
        }
      } else if (this.level === 'shop') {
        // 按省区+渠道编码去重
        const existing = new Map(this.rows.map((r) => [`${r.provinceCode}__${r.channels.map((c: any) => c.code).join(',')}`, r]));
        for (const nr of newRows) {
          const key = `${nr.provinceCode}__${nr.channels.map((c: any) => c.code).join(',')}`;
          if (existing.has(key)) {
            Object.assign(existing.get(key), nr);
          } else {
            this.rows.push(nr);
            existing.set(key, nr);
          }
        }
      } else {
        // 客户级：按客户编码+省区去重
        const existing = new Map(this.rows.map((r) => [`${r.code}__${r.provinceCode || ''}`, r]));
        for (const nr of newRows) {
          const key = `${nr.code}__${nr.provinceCode || ''}`;
          if (existing.has(key)) {
            Object.assign(existing.get(key), nr);
          } else {
            this.rows.push(nr);
            existing.set(key, nr);
          }
        }
      }
    },
    downloadTemplate() {
      if (!this.level) {
        ElMessage.warning('请先选择管控维度');
        return;
      }
      let templateData: Record<string, string>[] = [];
      let filename = '';
      if (this.level === 'channel') {
        templateData = [{
          '渠道类型编码（必填）': '',
          '渠道类型': '',
          '促销销售占比(%)': '',
          '让利占比-客户(%)': '',
          '让利占比-省区(%)': '',
          '老品让利幅度(%)': '',
          '新品让利幅度(%)': ''
        }];
        filename = '渠道级管控配置导入模板.xlsx';
      } else if (this.level === 'shop') {
        templateData = [{
          '省区编码（必填）': '',
          '省区名称': '',
          '渠道类型编码（必填）': '',
          '渠道类型': '',
          '销售办公室编码': '',
          '销售办公室名称': '',
          '促销销售占比(%)': '',
          '让利占比-客户(%)': '',
          '让利占比-省区(%)': '',
          '老品让利幅度(%)': '',
          '新品让利幅度(%)': ''
        }];
        filename = '省区级管控配置导入模板.xlsx';
      } else {
        templateData = [{
          '客户ERP编码（必填）': '',
          '客户名称': '',
          '省区编码（必填）': '',
          '省区名称': '',
          '销售办公室编码（必填）': '',
          '销售办公室名称': '',
          '渠道类型编码（必填）': '',
          '渠道类型': '',
          '促销销售占比(%)': '',
          '让利占比-客户(%)': '',
          '让利占比-省区(%)': '',
          '老品让利幅度(%)': '',
          '新品让利幅度(%)': ''
        }];
        filename = '客户级管控配置导入模板.xlsx';
      }
      exportExcelFile(templateData, { filename, bookType: 'xlsx' });
    },
    // ============ 批量填值 ============
    openBatchFillModal() {
      if (this.checkedRowCount === 0) {
        ElMessage.warning('请先勾选需要批量填值的行');
        return;
      }
      this.batchVals = { promo: '', margin: '', provMargin: '', oldProd: '', newProd: '' };
      this.batchFillVisible = true;
    },
    confirmBatchFill() {
      const v = this.batchVals;
      this.rows.filter((r) => r.checked).forEach((r) => {
        if (v.promo !== '') r.promo = v.promo;
        if (v.margin !== '') r.margin = v.margin;
        if (v.provMargin !== '') r.provMargin = v.provMargin;
        if (v.oldProd !== '') r.oldProd = v.oldProd;
        if (v.newProd !== '') r.newProd = v.newProd;
      });
      this.batchFillVisible = false;
      ElMessage.success(`已成功将数值批量应用到 ${this.checkedRowCount} 行数据！`);
    },
    // ============ 保存 ============
    buildRecords(): SalesRatioControlRecord[] {
      const recs: SalesRatioControlRecord[] = [];
      const num = (v: any) => (v === '' || v == null ? null : Number(v));
      for (const row of this.rows) {
        const thr = {
          promoSalesRatio: num(row.promo),
          customerMarginRatio: num(row.margin),
          provinceMarginRatio: num(row.provMargin),
          oldProductMarginRate: num(row.oldProd),
          newProductMarginRate: num(row.newProd)
        };
        if (this.level === 'channel') {
          (row.channels as any[]).forEach((c) => recs.push({ controlLevel: 'CHANNEL', channelType: c.name, channelTypeCode: c.code, ...thr }));
        } else if (this.level === 'shop') {
          const chans = row.channels.length ? row.channels : [{ code: '', name: '不限' }];
          const offs = row.salesOffices.length ? row.salesOffices : [{ code: '', name: '全部' }];
          chans.forEach((ch: any) =>
            offs.forEach((so: any) =>
              recs.push({
                controlLevel: 'PROVINCE',
                channelType: ch.name,
                channelTypeCode: ch.code,
                provincialBranchCode: row.provinceCode,
                provincialBranchName: row.provinceName,
                salesOfficeCode: so.name === '全部' ? '' : so.code,
                salesOfficeName: so.name === '全部' ? '不限' : so.name,
                ...thr
              })
            )
          );
        } else if (this.level === 'customer') {
          recs.push({
            controlLevel: 'CUSTOMER',
            channelType: row.channel || '不限',
            channelTypeCode: row.channelCode || '',
            provincialBranchCode: row.provinceCode,
            provincialBranchName: row.provinceName,
            salesOfficeCode: row.salesOfficeCode || '',
            salesOfficeName: row.soStr && row.soStr !== '-' ? row.soStr : '不限',
            customerCode: row.code,
            customerErpCode: row.erpCode,
            customerName: row.name,
            ...thr
          });
        }
      }
      return recs;
    },
    async saveNewConfig() {
      if (!this.level) {
        ElMessage.warning('请先选择管控维度并配置规则');
        return;
      }
      if (this.rows.length === 0) {
        ElMessage.warning('请添加至少一条配置规则！');
        return;
      }
      if (this.level === 'channel' && this.rows.some((r) => r.channels.length === 0)) {
        ElMessage.warning('存在未选择渠道类型的行');
        return;
      }
      const records = this.buildRecords();
      if (records.length === 0) {
        ElMessage.warning('没有可保存的配置');
        return;
      }
      this.saving = true;
      try {
        await batchSaveConfigs(this.moduleName, records);
        ElMessage.success(`保存成功！共提交 ${records.length} 条配置。`);
        this.cancel();
      } catch (e: any) {
        ElMessage.error(e?.message || '保存失败');
      } finally {
        this.saving = false;
      }
    },
    cancel() {
      if (this.backToList) (this.backToList as any)();
      else history.back();
    }
  }
});
